import {
  Component,
  computed,
  inject,
  Injector,
  runInInjectionContext,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatInputModule, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual, last, startCase } from 'lodash';
import { distinctUntilChanged, filter, skip, startWith, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../../../shared/components/datatable/datatable.component';
import { IDatatableColumn } from '../../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { EndpointResponseService } from '../../../../../shared/services/endpoint-response.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import { EWidgetRequestResultType } from '../../widget-requests/widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import { ITableViewMap, ITableViewMappingAddPayload } from '../table-view-widget-mappings.interface';
import { TableViewWidgetMappingAddEditDialogStore } from './table-view-widget-mapping-add-edit-dialog-store';
import {
  ITableViewMappingAddEditDialogData,
  IWidgetRequestMapRow,
} from './table-view-widget-mappings-add-edit-dialog.interface';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDropdown,
    DatatableComponent,
    MatTooltip,
  ],
  providers: [TableViewWidgetMappingAddEditDialogStore],
  selector: 'table-view-widget-mappings-add-edit-dialog',
  styleUrl: './table-view-widget-mappings-add-edit-dialog.component.scss',
  templateUrl: './table-view-widget-mappings-add-edit-dialog.component.html',
})
export class TableViewWidgetMappingsAddEditDialogComponent {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    maxWidth: '100%',
    restoreFocus: false,
    width: EDialogWidth.medium,
  };

  public readonly injectData = inject<ITableViewMappingAddEditDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly store = inject(TableViewWidgetMappingAddEditDialogStore);
  private readonly endpointResponseService = inject(EndpointResponseService);
  private readonly injector = inject(Injector);

  public readonly widgetRequestDropdownOptions = computed(() => {
    return this.store.widgetRequests().map(({ id, name }) => ({
      id,
      name,
    }));
  });

  public readonly pathOptionsStatus = signal(ELoadStatus.initial);
  public readonly widgetRequestDropdownSettings: Signal<IDropdownSettings> = computed(() => ({
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    // Render the error slot when the endpoint failed to load OR the required
    // field is invalid after the user interacted / submitted — same rule the
    // row dropdowns use, so the "required" message actually appears.
    hasMatError:
      this.pathOptionsStatus() === ELoadStatus.failure ||
      ((this.isFormSubmitted() || !this.addEditForm.controls.widgetRequestId.pristine) &&
        this.widgetRequestIdStatus() === 'INVALID'),
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.endpoint'),
  }));

  public readonly addEditForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    widgetRequestId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  // Tracks the endpoint control's validity as a signal so the dropdown error
  // slot (widgetRequestDropdownSettings.hasMatError) reacts to selection.
  private readonly widgetRequestIdStatus = toSignal(this.addEditForm.controls.widgetRequestId.statusChanges, {
    initialValue: this.addEditForm.controls.widgetRequestId.status,
  });

  public readonly dialogTitle = this.injectData.editData
    ? this.translate.instant('button.editContext', {
        context: this.translate.instant('field.table'),
      })
    : this.translate.instant('button.addContext', {
        context: this.translate.instant('field.table'),
      });

  public readonly submitButtonText = this.injectData.editData
    ? this.translate.instant('button.saveChanges')
    : this.translate.instant('button.addContext', {
        context: this.translate.instant('field.table'),
      });

  public readonly widgetRequestMapRowOrders: WritableSignal<string[]> = signal<string[]>([]);
  public readonly widgetRequestMapRows: WritableSignal<IWidgetRequestMapRow[]> = signal<IWidgetRequestMapRow[]>([]);

  public readonly orderedWidgetRequestMapRows = computed(() => {
    return this.widgetRequestMapRows()
      .map((fieldOption: IWidgetRequestMapRow) => ({ ...fieldOption, delete: false }))
      .sort((a, b) => this.widgetRequestMapRowOrders().indexOf(a.id) - this.widgetRequestMapRowOrders().indexOf(b.id));
  });

  public readonly widgetRequestMapDatatableColumns: IDatatableColumn<
    IWidgetRequestMapRow & {
      delete: boolean;
    },
    string
  >[] = [
    { id: 'id', isSortable: false },
    { id: 'widgetRequestContentPath' },
    { id: 'columnNameOnWidget' },
    { id: 'delete', isSortable: false },
  ];

  public readonly isFormSubmitted = signal(false, { equal: () => false });
  public readonly isMappingFormInvalid = computed(() => {
    this.isFormSubmitted();
    const orderedMappingIds = this.getOrderedMapRows();

    return orderedMappingIds.length === 0;
  });

  // Raw response of the selected endpoint — feeds both the property dropdowns and the JSON preview.
  public readonly endpointResponse = toSignal(
    this.addEditForm.controls.widgetRequestId.valueChanges.pipe(
      startWith(this.addEditForm.controls.widgetRequestId.value),
      filter((value) => Boolean(value.length)),
      map(([{ id }]) => id),
      distinctUntilChanged(),
      tap(() => {
        this.widgetRequestMapRows.set([]);
        this.widgetRequestMapRowOrders.set([]);
        this.addFieldOption();
        this.pathOptionsStatus.set(ELoadStatus.loading);
      }),
      switchMap((widgetRequestId) =>
        this.endpointResponseService.fetchResponse(widgetRequestId, this.pathOptionsStatus, {
          onSuccess: () => {
            this.enableOrDisableAllColumns(false);
            this.addEditForm.controls.widgetRequestId.setValidators([Validators.required]);
          },
          onFailure: () => {
            this.enableOrDisableAllColumns();
            this.addEditForm.controls.widgetRequestId.setValidators([
              Validators.required,
              (): Record<string, boolean> => ({ widgetRequestFailed: true }),
            ]);
          },
        }),
      ),
    ),
    { initialValue: null as unknown },
  );

  // First item of the response — the sample we extract property paths and example values from.
  private readonly endpointSampleItem = computed(() =>
    this.endpointResponseService.getSampleItem(this.endpointResponse()),
  );

  public readonly pathOptionsOfSelectedResource = computed(() =>
    this.endpointResponseService.buildPathOptions(this.endpointSampleItem()),
  );

  public readonly ELoadStatus = ELoadStatus;

  constructor() {
    const { editData } = this.injectData;

    if (editData) {
      this.addEditForm.reset({
        name: editData.name,
        widgetRequestId: [{ id: editData.widgetRequestId, name: '...' }],
      });

      this.widgetRequestMapRows.set([]);
      this.widgetRequestMapRowOrders.set([]);

      for (const path of editData.map) {
        this.addFieldOption(path);
      }
    } else {
      this.addFieldOption();
    }

    this.store.loadWidgetRequests({
      filters: [
        {
          field: 'resultType',
          ids: [EWidgetRequestResultType.multipleValues, EWidgetRequestResultType.multipleFields],
        },
      ],
    });

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });
  }

  public onFormDialogSubmit(): void {
    this.isFormSubmitted.set(true);

    for (const { columnNameOnWidget, widgetRequestContentPath } of this.orderedWidgetRequestMapRows()) {
      widgetRequestContentPath.markAsTouched();
      columnNameOnWidget.markAsTouched();
    }

    this.addEditForm.updateValueAndValidity();

    if (!this.addEditForm.valid || this.isMappingFormInvalid() || this.pathOptionsStatus() === ELoadStatus.failure) {
      return;
    }

    const payload = this.formatOnePayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  public addFieldOption(value?: ITableViewMap): void {
    const id: string = crypto.randomUUID();
    this.widgetRequestMapRowOrders.update((dashboardOptionOrders: string[]) => [...dashboardOptionOrders, id]);

    const pathFormControl = new FormControl(
      value?.widgetRequestContentPath
        ? [{ id: value.widgetRequestContentPath, name: value.widgetRequestContentPath }]
        : [],
      { nonNullable: true, validators: [Validators.required] },
    );

    const columnNameFormControl = new FormControl(value?.columnNameOnWidget ?? '', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
    });

    const pathValue = runInInjectionContext(this.injector, () => {
      pathFormControl.valueChanges
        .pipe(
          takeUntilDestroyed(),
          startWith(pathFormControl.value),
          filter((pathOption) => Boolean(pathOption.length)),
          distinctUntilChanged(isEqual),
          skip(value ? 1 : 0),
        )
        .subscribe((pathOption: [IDropdownOption<string>]) => {
          const [{ id: selectedPath }] = pathOption;
          const columnName = last(selectedPath.split('.'));
          columnNameFormControl.reset(startCase(columnName ?? ''));
        });

      return toSignal(pathFormControl.valueChanges, { initialValue: pathFormControl.value });
    });

    // Example value pulled from the endpoint's first item for this row's selected property.
    const exampleHint = computed(() =>
      this.endpointResponseService.buildExampleHint(this.endpointSampleItem(), pathValue()[0]?.id),
    );

    this.widgetRequestMapRows.update((dashboardOptions: IWidgetRequestMapRow[]) => [
      ...dashboardOptions,
      {
        columnNameOnWidget: columnNameFormControl,
        exampleHint,
        id,
        widgetRequestContentPath: pathFormControl,
      },
    ]);
  }

  public deleteMapRow(rowId: string): void {
    this.widgetRequestMapRowOrders.set(this.widgetRequestMapRowOrders().filter((id: string) => id !== rowId));
    this.widgetRequestMapRows.set(this.widgetRequestMapRows().filter((row) => row.id !== rowId));
  }

  private formatOnePayload(): ITableViewMappingAddPayload {
    const { value } = this.addEditForm;

    return {
      map: this.getOrderedMapRows(),
      name: FormUtilitiesService.trimIfString(value.name!),
      widgetRequestId: value.widgetRequestId![0].id,
    };
  }

  private getOrderedMapRows(): ITableViewMap[] {
    const widgetRequestMapRows = this.widgetRequestMapRows();

    if (
      widgetRequestMapRows.some(
        ({ columnNameOnWidget, widgetRequestContentPath }) =>
          columnNameOnWidget.invalid || widgetRequestContentPath.invalid,
      )
    ) {
      return [];
    }

    return widgetRequestMapRows
      .map((row) => ({
        columnNameOnWidget: row.columnNameOnWidget.value,
        id: row.id,
        widgetRequestContentPath: row.widgetRequestContentPath.value?.[0].id,
      }))
      .sort((a, b) => this.widgetRequestMapRowOrders().indexOf(a.id) - this.widgetRequestMapRowOrders().indexOf(b.id))
      .map(({ widgetRequestContentPath, columnNameOnWidget }) => ({ columnNameOnWidget, widgetRequestContentPath }));
  }

  public openEndpointPreview(): void {
    this.endpointResponseService.openPreview(this.endpointResponse());
  }

  private enableOrDisableAllColumns(disable = true): void {
    for (const { columnNameOnWidget, widgetRequestContentPath } of this.widgetRequestMapRows()) {
      if (disable) {
        columnNameOnWidget.disable();
        widgetRequestContentPath.disable();
      } else {
        columnNameOnWidget.enable();
        widgetRequestContentPath.enable();
      }
    }
  }
}
